import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Snackbar,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MonetizationOn as PriceIcon,
  LocalOffer as DiscountIcon,
  ShoppingBasket as BundleIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import AdminLayout from './components/AdminLayout';
import { 
  getPricingManagementDashboard, 
  createDiscountPlan, 
  updateDiscountPlan, 
  deleteDiscountPlan
} from '../../services/discountService';
import {
  updateSessionPrice
} from '../../services/sessionService';
import { setDefaultSessionPrice } from '../../services/configService';

// Interface for discount plan
interface DiscountPlan {
  id: number;
  name: string;
  description: string;
  minSessions: number;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  isActive: boolean;
}

// Interface for the pricing dashboard data
interface PricingDashboard {
  defaultSessionPrice: number;
  sessions: Array<{
    id: number;
    title: string;
    description: string;
    startDate: string;
    endDate: string;
    basePrice: number;
    acceptingEnrollment: boolean;
  }>;
  discountPlans: DiscountPlan[];
  bundleExamples: Array<{
    sessionCount: number;
    regularPrice: number;
    discountAmount: number;
    finalPrice: number;
    discountPlan: DiscountPlan | null;
  }>;
}

// Interface for new discount plan
interface NewDiscountPlan {
  name: string;
  description: string;
  minSessions: number;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  isActive: boolean;
}

function PricingManagementPage() {
  // State for pricing dashboard data
  const [dashboard, setDashboard] = useState<PricingDashboard | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for default price editing
  const [isEditingDefaultPrice, setIsEditingDefaultPrice] = useState<boolean>(false);
  const [newDefaultPrice, setNewDefaultPrice] = useState<string>('');
  const [savingDefaultPrice, setSavingDefaultPrice] = useState<boolean>(false);
  
  // State for session price editing
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editedSessionPrice, setEditedSessionPrice] = useState<string>('');
  const [savingSessionPrice, setSavingSessionPrice] = useState<boolean>(false);
  
  // State for discount plan dialog
  const [discountDialogOpen, setDiscountDialogOpen] = useState<boolean>(false);
  const [newDiscountPlan, setNewDiscountPlan] = useState<NewDiscountPlan>({
    name: '',
    description: '',
    minSessions: 2,
    discountType: 'fixed',
    discountValue: 0,
    isActive: true
  });
  const [editingDiscountId, setEditingDiscountId] = useState<number | null>(null);
  const [savingDiscountPlan, setSavingDiscountPlan] = useState<boolean>(false);
  
  // State for tab selection
  const [currentTab, setCurrentTab] = useState<number>(0);
  
  // State for notification
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false
    });
  };
  
  // Fetch data on component mount
  useEffect(() => {
    fetchPricingDashboard();
  }, []);
  
  // Function to fetch pricing dashboard data
  const fetchPricingDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getPricingManagementDashboard();
      setDashboard(data);
      
      // Initialize default price
      if (data && data.defaultSessionPrice) {
        setNewDefaultPrice(data.defaultSessionPrice.toString());
      }
    } catch (err) {
      console.error('Error fetching pricing dashboard:', err);
      setError('Failed to load pricing information. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle default price edit toggle
  const handleToggleDefaultPriceEdit = () => {
    if (isEditingDefaultPrice) {
      // Cancel editing
      setIsEditingDefaultPrice(false);
      
      // Reset to the current value
      if (dashboard) {
        setNewDefaultPrice(dashboard.defaultSessionPrice.toString());
      }
    } else {
      // Start editing
      setIsEditingDefaultPrice(true);
    }
  };
  
  // Handle default price change
  const handleDefaultPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and a single decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setNewDefaultPrice(value);
    }
  };
  
  // Save default price
  const handleSaveDefaultPrice = async () => {
    if (!dashboard) return;
    
    try {
      setSavingDefaultPrice(true);
      
      // Validate price
      const price = parseFloat(newDefaultPrice);
      if (isNaN(price) || price < 0) {
        showNotification('Please enter a valid price', 'error');
        return;
      }
      
      // Save price
      await setDefaultSessionPrice(price);
      
      // Update dashboard
      setDashboard({
        ...dashboard,
        defaultSessionPrice: price
      });
      
      // Show success message
      showNotification('Default price updated successfully', 'success');
      
      // Exit edit mode
      setIsEditingDefaultPrice(false);
    } catch (err) {
      console.error('Error saving default price:', err);
      showNotification('Failed to update default price', 'error');
    } finally {
      setSavingDefaultPrice(false);
    }
  };
  
  // Handle session price edit
  const handleEditSessionPrice = (sessionId: number, currentPrice: number) => {
    setEditingSessionId(sessionId);
    setEditedSessionPrice(currentPrice.toString());
  };
  
  // Handle session price change
  const handleSessionPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and a single decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setEditedSessionPrice(value);
    }
  };
  
  // Cancel session price edit
  const handleCancelSessionPriceEdit = () => {
    setEditingSessionId(null);
    setEditedSessionPrice('');
  };
  
  // Save session price
  const handleSaveSessionPrice = async () => {
    if (!dashboard || editingSessionId === null) return;
    
    try {
      setSavingSessionPrice(true);
      
      // Validate price
      const price = parseFloat(editedSessionPrice);
      if (isNaN(price) || price < 0) {
        showNotification('Please enter a valid price', 'error');
        return;
      }
      
      // Update session price
      await updateSessionPrice(editingSessionId, price);
      
      // Update dashboard
      const updatedSessions = dashboard.sessions.map(session => {
        if (session.id === editingSessionId) {
          return {
            ...session,
            basePrice: price
          };
        }
        return session;
      });
      
      setDashboard({
        ...dashboard,
        sessions: updatedSessions
      });
      
      // Show success message
      showNotification('Session price updated successfully', 'success');
      
      // Exit edit mode
      setEditingSessionId(null);
      setEditedSessionPrice('');
    } catch (err) {
      console.error('Error saving session price:', err);
      showNotification('Failed to update session price', 'error');
    } finally {
      setSavingSessionPrice(false);
    }
  };
  
  // Handle open discount plan dialog
  const handleOpenDiscountDialog = (discountPlan?: DiscountPlan) => {
    if (discountPlan) {
      // Editing existing plan
      setEditingDiscountId(discountPlan.id);
      setNewDiscountPlan({
        name: discountPlan.name,
        description: discountPlan.description,
        minSessions: discountPlan.minSessions,
        discountType: discountPlan.discountType,
        discountValue: discountPlan.discountValue,
        isActive: discountPlan.isActive
      });
    } else {
      // Creating new plan
      setEditingDiscountId(null);
      setNewDiscountPlan({
        name: '',
        description: '',
        minSessions: 2,
        discountType: 'fixed',
        discountValue: 0,
        isActive: true
      });
    }
    
    setDiscountDialogOpen(true);
  };
  
  // Handle close discount plan dialog
  const handleCloseDiscountDialog = () => {
    setDiscountDialogOpen(false);
    setEditingDiscountId(null);
  };
  
  // Handle discount plan field change
  const handleDiscountPlanChange = (field: keyof NewDiscountPlan, value: NewDiscountPlan[keyof NewDiscountPlan]) => {
    setNewDiscountPlan(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Save discount plan
  const handleSaveDiscountPlan = async () => {
    try {
      setSavingDiscountPlan(true);
      
      // Validate fields
      if (!newDiscountPlan.name || !newDiscountPlan.description) {
        showNotification('Name and description are required', 'error');
        return;
      }
      
      if (newDiscountPlan.minSessions < 1) {
        showNotification('Minimum sessions must be at least 1', 'error');
        return;
      }
      
      if (newDiscountPlan.discountValue < 0) {
        showNotification('Discount value cannot be negative', 'error');
        return;
      }
      
      if (newDiscountPlan.discountType === 'percentage' && newDiscountPlan.discountValue > 100) {
        showNotification('Percentage discount cannot exceed 100%', 'error');
        return;
      }
      
      if (editingDiscountId === null) {
        // Create new discount plan
        await createDiscountPlan(newDiscountPlan);
        showNotification('Discount plan created successfully', 'success');
      } else {
        // Update existing discount plan
        await updateDiscountPlan(editingDiscountId, newDiscountPlan);
        showNotification('Discount plan updated successfully', 'success');
      }
      
      // Refresh data
      fetchPricingDashboard();
      
      // Close dialog
      handleCloseDiscountDialog();
    } catch (err) {
      console.error('Error saving discount plan:', err);
      showNotification('Failed to save discount plan', 'error');
    } finally {
      setSavingDiscountPlan(false);
    }
  };
  
  // Delete discount plan
  const handleDeleteDiscountPlan = async (planId: number) => {
    if (!window.confirm('Are you sure you want to delete this discount plan?')) {
      return;
    }
    
    try {
      await deleteDiscountPlan(planId);
      
      // Update dashboard
      if (dashboard) {
        const updatedPlans = dashboard.discountPlans.filter(plan => plan.id !== planId);
        setDashboard({
          ...dashboard,
          discountPlans: updatedPlans
        });
      }
      
      showNotification('Discount plan deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting discount plan:', err);
      showNotification('Failed to delete discount plan', 'error');
    }
  };
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  
  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Pricing Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage session prices and bundle discounts.
        </Typography>
      </Box>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : dashboard ? (
        <>
          <Paper sx={{ mb: 4 }} data-demo-id="admin-pricing-tabs">
            <Tabs
              value={currentTab} 
              onChange={handleTabChange}
              variant="fullWidth"
            >
              <Tab icon={<PriceIcon />} label="Session Pricing" />
              <Tab icon={<DiscountIcon />} label="Bundle Discounts" />
              <Tab icon={<BundleIcon />} label="Example Bundles" />
            </Tabs>
          </Paper>
          
          {/* Session Pricing Tab */}
          {currentTab === 0 && (
            <>
              <Card elevation={2} sx={{ mb: 4 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5">
                      Default Session Price
                    </Typography>
                    <IconButton onClick={handleToggleDefaultPriceEdit}>
                      {isEditingDefaultPrice ? <CancelIcon /> : <EditIcon />}
                    </IconButton>
                  </Box>
                  
                  {isEditingDefaultPrice ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                      <TextField
                        label="Default Price"
                        value={newDefaultPrice}
                        onChange={handleDefaultPriceChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          )
                        }}
                        sx={{ minWidth: 200 }}
                      />
                      <Button 
                        variant="contained" 
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveDefaultPrice}
                        disabled={savingDefaultPrice}
                      >
                        {savingDefaultPrice ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="h4" color="primary">
                      ${parseFloat(String(dashboard.defaultSessionPrice)).toFixed(2)}
                    </Typography>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This is the default price for new sessions. Individual session prices can be set below.
                  </Typography>
                </CardContent>
              </Card>
              
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Session Prices
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Session</TableCell>
                          <TableCell>Date Range</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dashboard.sessions.map(session => (
                          <TableRow key={session.id}>
                            <TableCell>
                              <Typography variant="body1" fontWeight="medium">
                                {session.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {session.description.substring(0, 60)}{session.description.length > 60 ? '...' : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              {editingSessionId === session.id ? (
                                <TextField
                                  size="small"
                                  value={editedSessionPrice}
                                  onChange={handleSessionPriceChange}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">$</InputAdornment>
                                    )
                                  }}
                                  sx={{ width: 120 }}
                                />
                              ) : (
                                <Typography variant="body1" fontWeight="medium">
                                  ${parseFloat(String(session.basePrice)).toFixed(2)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {editingSessionId === session.id ? (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <IconButton 
                                    color="primary" 
                                    onClick={handleSaveSessionPrice}
                                    disabled={savingSessionPrice}
                                  >
                                    <SaveIcon />
                                  </IconButton>
                                  <IconButton 
                                    color="default" 
                                    onClick={handleCancelSessionPriceEdit}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Box>
                              ) : (
                                <IconButton 
                                  color="primary" 
                                  onClick={() => handleEditSessionPrice(session.id, session.basePrice)}
                                >
                                  <EditIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {dashboard.sessions.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body1" sx={{ py: 2 }}>
                                No sessions found.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}
          
          {/* Bundle Discounts Tab */}
          {currentTab === 1 && (
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">
                    Bundle Discount Plans
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDiscountDialog()}
                  >
                    Add Discount Plan
                  </Button>
                </Box>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Min Sessions</TableCell>
                        <TableCell>Discount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboard.discountPlans.map(plan => (
                        <TableRow key={plan.id}>
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {plan.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {plan.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {plan.minSessions}+
                          </TableCell>
                          <TableCell>
                            {plan.discountType === 'fixed' ? (
                              <Typography variant="body1">
                                ${parseFloat(String(plan.discountValue)).toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography variant="body1">
                                {plan.discountValue}%
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={plan.isActive ? 'Active' : 'Inactive'}
                              color={plan.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleOpenDiscountDialog(plan)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                color="error" 
                                onClick={() => handleDeleteDiscountPlan(plan.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {dashboard.discountPlans.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body1" sx={{ py: 2 }}>
                              No discount plans found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
          
          {/* Example Bundles Tab */}
          {currentTab === 2 && (
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">
                    Example Bundle Pricing
                  </Typography>
                  <Tooltip title="These examples show how your current discount plans affect session bundles">
                    <InfoIcon color="action" />
                  </Tooltip>
                </Box>
                
                <Grid container spacing={3}>
                  {dashboard.bundleExamples.map((example, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 3, 
                          textAlign: 'center', 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: example.discountAmount > 0 ? 'success.50' : 'background.paper'
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          {example.sessionCount} {example.sessionCount === 1 ? 'Session' : 'Sessions'}
                        </Typography>
                        
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          {example.discountAmount > 0 && (
                            <>
                              <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                ${parseFloat(String(example.regularPrice)).toFixed(2)}
                              </Typography>
                              <Typography variant="body1" color="success.main">
                                Save ${parseFloat(String(example.discountAmount)).toFixed(2)}
                              </Typography>
                            </>
                          )}
                          
                          <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
                            ${parseFloat(String(example.finalPrice)).toFixed(2)}
                          </Typography>
                        </Box>
                        
                        {example.discountPlan && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                            Plan: {example.discountPlan.name}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
          
          {/* Discount Plan Dialog */}
          <Dialog 
            open={discountDialogOpen} 
            onClose={handleCloseDiscountDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {editingDiscountId === null ? 'Create New Discount Plan' : 'Edit Discount Plan'}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <TextField 
                  label="Name"
                  value={newDiscountPlan.name}
                  onChange={(e) => handleDiscountPlanChange('name', e.target.value)}
                  fullWidth
                  required
                />
                
                <TextField 
                  label="Description"
                  value={newDiscountPlan.description}
                  onChange={(e) => handleDiscountPlanChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  required
                />
                
                <TextField 
                  label="Minimum Sessions"
                  type="number"
                  value={newDiscountPlan.minSessions}
                  onChange={(e) => handleDiscountPlanChange('minSessions', parseInt(e.target.value, 10))}
                  InputProps={{ inputProps: { min: 1 } }}
                  fullWidth
                  required
                  helperText="Minimum number of sessions required to qualify for this discount"
                />
                
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={newDiscountPlan.discountType}
                    onChange={(e) => handleDiscountPlanChange('discountType', e.target.value)}
                    label="Discount Type"
                  >
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                    <MenuItem value="percentage">Percentage</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField 
                  label={newDiscountPlan.discountType === 'fixed' ? 'Discount Amount' : 'Discount Percentage'}
                  type="number"
                  value={newDiscountPlan.discountValue}
                  onChange={(e) => handleDiscountPlanChange('discountValue', parseFloat(e.target.value))}
                  InputProps={{ 
                    inputProps: { 
                      min: 0,
                      max: newDiscountPlan.discountType === 'percentage' ? 100 : undefined 
                    },
                    startAdornment: newDiscountPlan.discountType === 'fixed' ? (
                      <InputAdornment position="start">$</InputAdornment>
                    ) : undefined,
                    endAdornment: newDiscountPlan.discountType === 'percentage' ? (
                      <InputAdornment position="end">%</InputAdornment>
                    ) : undefined
                  }}
                  fullWidth
                  required
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={newDiscountPlan.isActive}
                      onChange={(e) => handleDiscountPlanChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDiscountDialog}>Cancel</Button>
              <Button 
                variant="contained"
                onClick={handleSaveDiscountPlan}
                disabled={savingDiscountPlan}
                startIcon={savingDiscountPlan ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {savingDiscountPlan ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </AdminLayout>
  );
}

export default PricingManagementPage;